import React from 'react'
import styled from '@emotion/styled'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { InfoHeader } from '@mobi/component-library/Typography/InfoHeader'
import { font, spacing } from '@mobi/component-library/Foundations'
import { hexColors } from '@mobi/settings/hexColors'
import { appHistory } from '@core/Utils/routing/history'
import type { StructuredSearchResult } from '@core/Areas/StructuredSearch/types'
import { ResultContentListRow } from '@core/Areas/Search/Components/Common/ResultContentListRow'
import { getRedirectUrl } from './helpers'
import { StructuredLocationTime } from './Components/StructuredLocationTime'
import { StructuredSearchSilkImage } from './Components/StructuredSearchSilkImage'

export const StructuredSearchResults: React.FC<{ results: StructuredSearchResult[] }> = ({
  results,
}) => {
  if (!results || results.length === 0) {
    return (
      <div style={{ paddingTop: spacing.lg }}>
        <InfoHeader
          size='large'
          icon='warning'
          header='No Results Found'
          subHeader='Try making your criteria less specific'
        />
      </div>
    )
  }

  return results.map(result => (
    <WrapperStyled
      key={result.id}
      onClick={() => handleClick(result)}
      data-tid-structured-search-result={result.id}
    >
      <ResultContentListRow
        topContent={
          <StructuredLocationTime
            locationName={result.acceptor.race.fixture.name}
            raceNumber={result.acceptor.race.number}
            advertisedStartTime={result.acceptor.race.advertisedStartTime}
            code={result.acceptor.race.fixture.contestType}
          />
        }
        leftContent={
          <StructuredSearchSilkImage
            meetingId={result.acceptor.race.fixture.id}
            meetingDate={result.acceptor.race.fixture.date}
            raceNo={result.acceptor.race.number}
            isDogsSilk={result.acceptor.race.fixture.contestType === 'Dogs'}
            isTrots={result.acceptor.race.fixture.contestType === 'Trots'}
            acceptorNumber={result.acceptor.numberOrBox}
          />
        }
        primaryText={`${result.acceptor.numberOrBox} - ${result.acceptor.name}`}
        secondaryText={
          <>
            {result.acceptor.riderName && <span>J: {result.acceptor.riderName}</span>}
            {result.acceptor.driverName && <span>D: {result.acceptor.driverName}</span>}
            {result.acceptor.trainerName && <span>T: {result.acceptor.trainerName}</span>}
          </>
        }
        rightContent={
          result.acceptor.fixedOddsPrices &&
          (result.acceptor.fixedOddsPrices.win.isValid ||
            result.acceptor.fixedOddsPrices.place.isValid) && (
            <Flex flexDirection='column' alignItems='flex-end'>
              <span style={{ fontWeight: font.weight.medium }}>
                {!result.acceptor.fixedOddsPrices.win.isValid
                  ? '-'
                  : result.acceptor.fixedOddsPrices.win?.dividend?.toFixed(2)}
              </span>
              <span style={{ fontWeight: font.weight.medium }}>
                {!result.acceptor.fixedOddsPrices.place.isValid
                  ? '-'
                  : result.acceptor.fixedOddsPrices.place?.dividend?.toFixed(2)}
              </span>
            </Flex>
          )
        }
      />
    </WrapperStyled>
  ))
}

// =============
// Local Helpers
// =============

function handleClick(searchResult: StructuredSearchResult) {
  const url = getRedirectUrl(searchResult)
  appHistory.push(url)
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  fontFamily: font.family.primary,
  fontSize: font.size.lg.fontSize,
  letterSpacing: font.size.lg.letterSpacing,
  lineHeight: font.size.lg.lineHeight,
  backgroundColor: hexColors.white,
  padding: '0.5rem 0.75rem',
  borderBottom: `1px solid ${hexColors.gainsboro}`,
})
